'use client'

import React, { useRef, useCallback, useState } from 'react'
import Image from 'next/image'
import type { LocationsBlock as LocationsBlockProps } from '@/payload-types'
import { cn } from '@/utilities/ui'
import { getBlockPadding } from '@/utilities/getBlockPadding'
import { getSectionHeadingLevels } from '@/blocks/SectionHeader/getSectionHeadingLevels'
import { InteractiveMap } from './InteractiveMap'
import type { InteractiveMapHandle, LocationPin } from './InteractiveMap'
import { cityCoordinates } from './cityCoordinates'

type Props = LocationsBlockProps & {
  disableInnerContainer?: boolean
  hasHero?: boolean
  isFirstSectionHeader?: boolean
}

export const LocationsBlock: React.FC<Props> = ({
  subHeading,
  heading,
  description,
  showMap = true,
  offices,
  regions,
  disableInnerContainer: _disableInnerContainer,
  hasHero = true,
  isFirstSectionHeader = false,
  paddingTop,
  paddingBottom,
}) => {
  // Hooks must be called before any early returns
  const mapRefMobile = useRef<InteractiveMapHandle>(null)
  const mapRefDesktop = useRef<InteractiveMapHandle>(null)
  const [activeLocation, setActiveLocation] = useState<string | null>(null)

  const handleLocationClick = useCallback((name: string, key: string) => {
    const coords = cityCoordinates[name]
    if (!coords) return
    setActiveLocation(key)
    mapRefMobile.current?.flyToLocation(coords.longitude, coords.latitude)
    mapRefDesktop.current?.flyToLocation(coords.longitude, coords.latitude)
  }, [])

  // Get padding classes based on layout options
  const paddingClasses = getBlockPadding({
    paddingTop: paddingTop ?? true,
    paddingBottom: paddingBottom ?? true,
  })

  if (!subHeading && !heading && !description && (!offices || offices.length === 0) && (!regions || regions.length === 0)) {
    return null
  }

  const { headingLevel: HeadingTag, eyebrowLevel: EyebrowTag } = getSectionHeadingLevels({ hasHero, isFirstSectionHeader, hasHeading: Boolean(heading) })
  const SubSectionTag = HeadingTag === 'h1' ? 'h2' : 'h3'

  // Build map pins from city name lookup, merging co-located offices and regions into combined markers
  const officeNames = new Set(offices || [])
  const regionNames = new Set(regions || [])
  const allCityNames = new Set([...officeNames, ...regionNames])

  const mapPins: LocationPin[] = []
  for (const city of allCityNames) {
    const coords = cityCoordinates[city]
    if (!coords) continue
    const displayName = city
    const isOffice = officeNames.has(city)
    const isRegion = regionNames.has(city)
    if (isOffice && isRegion) {
      mapPins.push({ name: displayName, ...coords, type: 'combined' })
    } else if (isOffice) {
      mapPins.push({ name: displayName, ...coords, type: 'office' })
    } else {
      mapPins.push({ name: displayName, ...coords, type: 'region' })
    }
  }

  const hasMapPins = mapPins.length > 0

  return (
    <div className={cn("box-border flex flex-col gap-7 md:gap-16 items-start justify-start relative w-full", paddingClasses)}>
      {/* Sub Heading with Line - Full Width */}
      {subHeading && (
        <div className="flex flex-col gap-2 items-start justify-start w-full">
          <div className="subheading-xsmall text-black w-full">
            <EyebrowTag className="mb-0 leading-5">{subHeading}</EyebrowTag>
          </div>
          <div className="h-px w-full border-t border-[#d1ccbe]" />
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-7 md:gap-16 items-start justify-start w-full">

        {/* Content Section - Left on desktop, Top on mobile */}
        <div className="flex flex-col gap-7 md:gap-9 items-start justify-start w-full md:w-[527px] md:flex-shrink-0 md:justify-between md:min-h-[453px]">

          {/* Top content group */}
          <div className="flex flex-col gap-7 w-full">
            {/* Text content */}
            {(heading || description) && (
              <div className="w-full flex flex-col gap-4">
                {heading && (
                  <div className="display-large">
                    <HeadingTag>{heading}</HeadingTag>
                  </div>
                )}
                {description && (
                  <p className="body-medium text-black/70 max-w-[527px]">
                    {description}
                  </p>
                )}
              </div>
            )}

            {/* Map for mobile - placed after content */}
            {showMap && (
              <div className="block md:hidden w-full">
                {hasMapPins ? (
                  <InteractiveMap ref={mapRefMobile} pins={mapPins} activeLocationKey={activeLocation} onReset={() => setActiveLocation(null)} className="h-72 w-full rounded-[8px] overflow-hidden" />
                ) : (
                  <div className="relative w-full h-52 rounded-[8px] overflow-hidden">
                    <Image
                      src="/assets-rebrand/images/Map_Light.png"
                      alt="World map showing global presence"
                      fill
                      className="object-cover"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Offices and Regions Section - Bottom on desktop */}
          <div className="flex flex-col gap-8 md:flex-row md:gap-16 w-full">

            {/* Offices */}
            {offices && offices.length > 0 && (
              <div className="flex flex-col gap-3 w-full md:flex-1">
                <div className="flex items-center gap-3.5">
                  <div className="w-5 h-6 flex-shrink-0">
                    <Image
                      src="/assets-rebrand/icons/location-black.svg"
                      alt="Office location"
                      width={20.163}
                      height={22.946}
                      className="w-full h-full"
                    />
                  </div>
                  <SubSectionTag className="subheading-large text-black">Offices</SubSectionTag>
                </div>
                <div className="grid grid-cols-2 gap-x-7 gap-y-3 w-full">
                  {offices.map((office) => (
                    <button
                      key={office}
                      className={cn(
                        "body-large cursor-pointer rounded-full px-3 py-1 transition-all duration-300 ease-in-out text-left w-fit",
                        activeLocation === `office-${office}`
                          ? "bg-civo-black text-civo-lime"
                          : "text-black/70 hover:text-black",
                      )}
                      onClick={() => handleLocationClick(office, `office-${office}`)}
                    >
                      {office}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Regions */}
            {regions && regions.length > 0 && (
              <div className="flex flex-col gap-3 w-full md:flex-1">
                <div className="flex items-center gap-3.5">
                  <div className="w-5 h-6 flex-shrink-0">
                    <Image
                      src="/assets-rebrand/icons/location-green.svg"
                      alt="Region location"
                      width={20.163}
                      height={22.946}
                      className="w-full h-full"
                    />
                  </div>
                  <SubSectionTag className="subheading-large text-black">Regions</SubSectionTag>
                </div>
                <div className="grid grid-cols-2 gap-x-7 gap-y-3 w-full">
                  {regions.map((region) => (
                    <button
                      key={region}
                      className={cn(
                        "body-large cursor-pointer rounded-full px-3 py-1 transition-all duration-300 ease-in-out text-left w-fit",
                        activeLocation === `region-${region}`
                          ? "bg-civo-black text-civo-lime"
                          : "text-black/70 hover:text-black",
                      )}
                      onClick={() => handleLocationClick(region, `region-${region}`)}
                    >
                      {region}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Map Section - Right on desktop, Hidden on mobile */}
        {showMap && (
          <div className="hidden md:block w-full md:flex-1">
            {hasMapPins ? (
              <InteractiveMap ref={mapRefDesktop} pins={mapPins} activeLocationKey={activeLocation} onReset={() => setActiveLocation(null)} className="h-[453px] w-full rounded-[8px] overflow-hidden" />
            ) : (
              <div className="relative w-full h-[453px] rounded-[8px] overflow-hidden">
                <Image
                  src="/assets-rebrand/images/Map_Light.png"
                  alt="World map showing global presence"
                  fill
                  className="object-cover"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
